// ============================================================
// NotificacionesPage — Diseño SAE handoff (Indigo + Teal)
// Switcher pills entre Notificaciones del sistema / Comunicados,
// tabs por filtro, lista a la izquierda + detalle/estadísticas
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { useNotificaciones } from 'contexts/NotificacionContext';
import {
  NotificacionAdmin, NotificacionMia,
  TipoAudiencia, CategoriaNotificacion,
} from 'types/notificacion.types';
import { notificacionService } from 'services/notificacionService';
import CrearNotificacionPage from 'pages/CrearNotificacionPage';
import { getConfig } from 'utils/appConfig';
import { I, PageHead, Card, Badge, Progress, EmptyState } from 'components/ui';

const ROLES_PUEDEN_CREAR = ['ADMIN', 'DIRECTOR', 'SECRETARIA', 'DOCENTE'];

// ── Tipos locales ─────────────────────────────────────────────

type Section       = 'notificaciones' | 'comunicados';
type FiltroUsuario = 'todas' | 'no_leidas' | 'urgentes';

// ── Helpers de formato ────────────────────────────────────────

const tiempoRelativo = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const min  = Math.floor(diff / 60_000);
  if (min < 1)  return 'ahora';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24)   return `${h} h`;
  const d = Math.floor(h / 24);
  if (d === 1)  return 'ayer';
  return new Date(iso).toLocaleDateString('es-PE', { day: '2-digit', month: 'short' });
};

const fmtFecha = (iso: string) =>
  new Date(iso).toLocaleString('es-PE', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const fmtFechaDetalle = (iso: string) =>
  new Date(iso).toLocaleString('es-PE', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

// ── Mapeos visuales ───────────────────────────────────────────

const CATEGORIA_BADGE: Record<CategoriaNotificacion, { label: string; kind: 'info' | 'danger' | 'warning' }> = {
  COMUNICADO:   { label: 'Comunicado',   kind: 'info' },
  ALERTA:       { label: 'Alerta',       kind: 'danger' },
  RECORDATORIO: { label: 'Recordatorio', kind: 'warning' },
};

const CATEGORIA_COLOR: Record<CategoriaNotificacion, { bg: string; fg: string; icon: keyof typeof I }> = {
  COMUNICADO:   { bg: 'var(--info-bg)',    fg: 'var(--info)',    icon: 'Megaphone' },
  ALERTA:       { bg: 'var(--danger-bg)',  fg: 'var(--danger)',  icon: 'AlertCircle' },
  RECORDATORIO: { bg: 'var(--warning-bg)', fg: 'var(--warning)', icon: 'Clock' },
};

const AUDIENCIA_LABEL: Record<TipoAudiencia, string> = {
  TODOS: 'Todos', ROL: 'Por Rol', NIVEL: 'Por Nivel',
  GRADO: 'Por Grado', SECCION: 'Por Sección', INDIVIDUAL: 'Individual',
};

const esUrgente = (n: NotificacionMia) =>
  n.categoria === 'ALERTA' ||
  (!n.leida && Date.now() - new Date(n.fechaCreacion).getTime() < 4 * 60 * 60_000);

// ─────────────────────────────────────────────────────────────────
// Vista del usuario final (todos excepto admin)
// ─────────────────────────────────────────────────────────────────

const VistaUsuario: React.FC<{ puedeCrear?: boolean; onCrear?: () => void }> = ({ puedeCrear, onCrear }) => {
  const { notificaciones, noLeidas, sinConfirmar, loading, marcarLeida, marcarTodasLeidas, confirmar } =
    useNotificaciones();

  const [section, setSection]           = useState<Section>('notificaciones');
  const [filtro, setFiltro]             = useState<FiltroUsuario>('todas');
  const [seleccionada, setSeleccionada] = useState<NotificacionMia | null>(null);
  const [confirmando, setConfirmando]   = useState(false);

  const isComm = section === 'comunicados';

  // Filtro por sección + filtro local
  const baseList = isComm
    ? notificaciones.filter(n => n.categoria === 'COMUNICADO')
    : notificaciones.filter(n => n.categoria !== 'COMUNICADO');

  const filtradas = baseList.filter(n => {
    if (filtro === 'no_leidas') return !n.leida;
    if (filtro === 'urgentes')  return esUrgente(n);
    return true;
  });

  const noLeidasSection = baseList.filter(n => !n.leida).length;
  const urgentesSection = baseList.filter(esUrgente).length;
  const totalComunicados = notificaciones.filter(n => n.categoria === 'COMUNICADO').length;
  const totalSistema = notificaciones.filter(n => n.categoria !== 'COMUNICADO').length;

  const handleSeleccionar = (n: NotificacionMia) => {
    setSeleccionada(n);
    if (!n.leida) marcarLeida(n.idNotificacion);
  };

  const handleConfirmar = async () => {
    if (!seleccionada) return;
    setConfirmando(true);
    await confirmar(seleccionada.idNotificacion);
    setSeleccionada(prev =>
      prev ? { ...prev, confirmada: true, fechaConfirmacion: new Date().toISOString() } : prev
    );
    setConfirmando(false);
  };

  const institucion = getConfig().institucionNombre || 'Institución Educativa';

  return (
    <>
      {/* Cabecera */}
      <PageHead
        title={isComm ? 'Comunicados' : 'Notificaciones'}
        sub={
          isComm
            ? `${totalComunicados} comunicado${totalComunicados !== 1 ? 's' : ''} · ${baseList.filter(n => !n.leida).length} sin leer`
            : `${noLeidas} sin leer · ${baseList.length} en total${
                sinConfirmar > 0 ? ` · ${sinConfirmar} sin confirmar` : ''
              }`
        }
        actions={
          <>
            {noLeidas > 0 && (
              <button onClick={() => marcarTodasLeidas()} className="btn btn-secondary btn-hide-mobile">
                <I.Check size={14} /> Marcar todo como leído
              </button>
            )}
            {puedeCrear && onCrear && (
              <button onClick={onCrear} className="btn btn-primary">
                {isComm ? <I.Megaphone size={14} /> : <I.Plus size={14} />}
                {isComm ? 'Nuevo comunicado' : 'Nueva'}
              </button>
            )}
          </>
        }
      />

      {/* Switcher Notificaciones / Comunicados */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <button
          className={`pill ${!isComm ? 'active' : ''}`}
          onClick={() => { setSection('notificaciones'); setFiltro('todas'); setSeleccionada(null); }}
        >
          <I.Bell size={13} /> Notificaciones del sistema
          <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-white/20">
            {totalSistema}
          </span>
        </button>
        <button
          className={`pill ${isComm ? 'active' : ''}`}
          onClick={() => { setSection('comunicados'); setFiltro('todas'); setSeleccionada(null); }}
        >
          <I.Megaphone size={13} /> Comunicados institucionales
          <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-white/20">
            {totalComunicados}
          </span>
        </button>
      </div>

      {/* Tabs de filtro */}
      <div className="tabs">
        <button className={`tab ${filtro === 'todas' ? 'active' : ''}`} onClick={() => setFiltro('todas')}>
          Todos <span className="ml-1 text-ink-400 text-xs">({baseList.length})</span>
        </button>
        <button className={`tab ${filtro === 'no_leidas' ? 'active' : ''}`} onClick={() => setFiltro('no_leidas')}>
          No leídos <span className="ml-1 text-ink-400 text-xs">({noLeidasSection})</span>
        </button>
        {!isComm && (
          <button className={`tab ${filtro === 'urgentes' ? 'active' : ''}`} onClick={() => setFiltro('urgentes')}>
            Urgentes <span className="ml-1 text-ink-400 text-xs">({urgentesSection})</span>
          </button>
        )}
      </div>

      {/* Layout: lista + detalle */}
      <div className="grid-12">
        {/* Lista */}
        <Card pad={false}>
          {loading ? (
            <div className="flex justify-center py-16">
              <I.Activity size={32} className="animate-pulse text-ink-300" />
            </div>
          ) : filtradas.length === 0 ? (
            <EmptyState
              icon="Bell"
              title={
                filtro === 'no_leidas' ? 'Todo leído'
                : filtro === 'urgentes' ? 'Sin alertas urgentes'
                : isComm ? 'Sin comunicados'
                : 'Bandeja vacía'
              }
            />
          ) : (
            <div className="grid gap-px" style={{ background: 'var(--border)' }}>
              {filtradas.map(n => {
                const cat     = CATEGORIA_COLOR[n.categoria];
                const IconC   = I[cat.icon];
                const activa  = seleccionada?.idNotificacion === n.idNotificacion;
                const urgente = esUrgente(n);
                const pendConf = n.requiereConfirmacion && !n.confirmada;

                return (
                  <button
                    key={n.idNotificacion}
                    onClick={() => handleSeleccionar(n)}
                    className="text-left transition-colors"
                    style={{
                      background: activa ? 'var(--primary-50)' : 'white',
                      borderLeft: activa
                        ? '3px solid var(--primary-600)'
                        : !n.leida
                          ? '3px solid var(--primary-400)'
                          : '3px solid transparent',
                      padding: '14px 18px',
                    }}
                  >
                    <div className="flex gap-3.5 items-start">
                      <div
                        className="rounded-r grid place-items-center flex-shrink-0"
                        style={{
                          width: 40, height: 40,
                          background: cat.bg,
                          color: cat.fg,
                        }}
                      >
                        <IconC size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="row mb-1 flex-wrap" style={{ gap: 6 }}>
                          {urgente
                            ? <Badge kind="danger" dot>Urgente</Badge>
                            : <Badge kind={CATEGORIA_BADGE[n.categoria].kind}>{CATEGORIA_BADGE[n.categoria].label}</Badge>
                          }
                          {pendConf && <Badge kind="warning">Confirmar</Badge>}
                          {!n.leida && !urgente && <Badge kind="indigo" dot>Nuevo</Badge>}
                        </div>
                        <div
                          className={`text-[13.5px] mb-0.5 ${!n.leida ? 'font-bold text-ink-900' : 'font-semibold text-ink-700'}`}
                        >
                          {n.titulo}
                        </div>
                        <div
                          className="text-[12px] text-ink-500 leading-relaxed line-clamp-2"
                          dangerouslySetInnerHTML={{
                            __html: n.mensaje.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(),
                          }}
                        />
                        <div className="row mt-1.5 text-[11px] text-ink-400" style={{ gap: 10 }}>
                          <span><I.Clock size={11} /> {tiempoRelativo(n.fechaCreacion)}</span>
                          <span><I.Send size={11} /> {AUDIENCIA_LABEL[n.tipoAudiencia]}</span>
                          {n.adjuntoUrl && <span><I.FileText size={11} /> Adjunto</span>}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        {/* Detalle / estadísticas */}
        <Card title={seleccionada ? 'Detalle' : 'Resumen'}>
          {seleccionada ? (
            <div>
              {/* Remitente */}
              <div className="row mb-4 pb-4 border-b" style={{ borderColor: 'var(--border)' }}>
                <div
                  className="rounded-full grid place-items-center font-bold text-sm flex-shrink-0"
                  style={{ width: 40, height: 40, background: 'var(--primary-100)', color: 'var(--primary-700)' }}
                >
                  {institucion.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-ink-900 text-sm leading-tight m-0">{institucion}</p>
                  <p className="text-xs text-ink-500 mt-0.5 m-0">{fmtFechaDetalle(seleccionada.fechaCreacion)}</p>
                </div>
              </div>

              {/* Título */}
              <h2 className="text-lg font-extrabold text-ink-900 leading-tight mb-3" style={{ letterSpacing: '-.01em' }}>
                {seleccionada.titulo}
              </h2>

              {/* Badges */}
              <div className="row mb-4 flex-wrap" style={{ gap: 6 }}>
                {esUrgente(seleccionada)
                  ? <Badge kind="danger" dot>Urgente</Badge>
                  : <Badge kind={CATEGORIA_BADGE[seleccionada.categoria].kind}>
                      {CATEGORIA_BADGE[seleccionada.categoria].label}
                    </Badge>
                }
                <Badge kind="indigo">{AUDIENCIA_LABEL[seleccionada.tipoAudiencia]}</Badge>
                {seleccionada.fechaExpiracion && (
                  <Badge kind="warning">Expira {seleccionada.fechaExpiracion}</Badge>
                )}
              </div>

              {/* Cuerpo */}
              <div
                className="text-sm text-ink-700 leading-relaxed
                           [&_h1]:text-base [&_h1]:font-bold [&_h2]:text-sm [&_h2]:font-bold
                           [&_a]:text-primary-700 [&_a]:underline [&_strong]:text-ink-900
                           [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                dangerouslySetInnerHTML={{ __html: seleccionada.mensaje }}
              />

              {/* Adjunto */}
              {seleccionada.adjuntoUrl && (
                <a
                  href={seleccionada.adjuntoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary mt-4 inline-flex"
                >
                  <I.FileText size={14} /> Ver adjunto
                </a>
              )}

              {/* Bloque de confirmación */}
              {seleccionada.requiereConfirmacion && (
                <div
                  className="mt-5 rounded-r p-3.5"
                  style={{
                    background: seleccionada.confirmada ? 'var(--success-bg)' : 'var(--warning-bg)',
                    border: `1px solid ${seleccionada.confirmada ? 'var(--success)' : 'var(--warning)'}`,
                  }}
                >
                  {seleccionada.confirmada ? (
                    <div className="row" style={{ gap: 12 }}>
                      <I.CheckCircle size={20} style={{ color: 'var(--success)', flexShrink: 0 }} />
                      <div>
                        <p className="text-sm font-bold m-0" style={{ color: '#166534' }}>
                          Comunicado confirmado
                        </p>
                        {seleccionada.fechaConfirmacion && (
                          <p className="text-xs mt-0.5 m-0" style={{ color: '#166534', opacity: 0.7 }}>
                            {fmtFechaDetalle(seleccionada.fechaConfirmacion)}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold m-0" style={{ color: '#92400e' }}>
                          Requiere confirmación
                        </p>
                        <p className="text-xs mt-0.5 m-0" style={{ color: '#92400e', opacity: 0.8 }}>
                          Al confirmar registramos que leíste este comunicado.
                        </p>
                      </div>
                      <button
                        onClick={handleConfirmar}
                        disabled={confirmando}
                        className="btn btn-primary btn-sm flex-shrink-0"
                        style={{ background: 'var(--warning)' }}
                      >
                        {confirmando
                          ? <I.Activity size={14} className="animate-pulse" />
                          : <I.Check size={14} />}
                        He leído y entendido
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            // Resumen / empty state
            <div className="grid gap-2.5 text-[13px]">
              <div className="row between"><span className="muted">Total {isComm ? 'comunicados' : 'notificaciones'}</span><b>{baseList.length}</b></div>
              <div className="row between"><span className="muted">Sin leer</span><b className="text-primary-700">{noLeidasSection}</b></div>
              {!isComm && (
                <div className="row between"><span className="muted">Urgentes</span>
                  <b style={{ color: 'var(--danger)' }}>{urgentesSection}</b>
                </div>
              )}
              {sinConfirmar > 0 && (
                <div className="row between"><span className="muted">Pendientes de confirmar</span>
                  <b style={{ color: 'var(--warning)' }}>{sinConfirmar}</b>
                </div>
              )}
              <div className="divider" />
              <p className="text-xs text-ink-400 text-center m-0">
                Selecciona una {isComm ? 'comunicado' : 'notificación'} para ver el detalle
              </p>
            </div>
          )}
        </Card>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────
// Vista admin
// ─────────────────────────────────────────────────────────────────

const audienciaDetalle = (n: NotificacionAdmin): string => {
  if (n.rolNombre)            return `Rol: ${n.rolNombre}`;
  if (n.nivel)                return `Nivel: ${n.nivel}`;
  if (n.gradoNombre)          return `Grado: ${n.gradoNombre}`;
  if (n.seccionNombre)        return `Sección: ${n.seccionNombre}`;
  if (n.usuarioDestinoNombre) return `→ ${n.usuarioDestinoNombre}`;
  return '—';
};

const VistaAdmin: React.FC<{ onCrear: () => void }> = ({ onCrear }) => {
  const [items, setItems]               = useState<NotificacionAdmin[]>([]);
  const [loading, setLoading]           = useState(true);
  const [soloActivos, setSoloActivos]   = useState(true);
  const [confirmId, setConfirmId]       = useState<number | null>(null);
  const [desactivando, setDesactivando] = useState(false);
  const [filtroCat, setFiltroCat]       = useState<'TODOS' | CategoriaNotificacion>('TODOS');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await notificacionService.getAdmin();
      setItems(data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleDesactivar = async (id: number) => {
    setDesactivando(true);
    try {
      await notificacionService.desactivar(id);
      setItems(prev => prev.map(n => n.idNotificacion === id ? { ...n, activo: false } : n));
      setConfirmId(null);
    } catch {} finally { setDesactivando(false); }
  };

  const filtrados = items
    .filter(n => !soloActivos || n.activo)
    .filter(n => filtroCat === 'TODOS' || n.categoria === filtroCat);

  // Estadísticas
  const totalActivas        = items.filter(n => n.activo).length;
  const totalLecturas       = items.reduce((acc, n) => acc + (n.totalLecturas ?? 0), 0);
  const totalConfirmaciones = items.reduce((acc, n) => acc + (n.totalConfirmaciones ?? 0), 0);
  const requierenConf       = items.filter(n => n.requiereConfirmacion).length;

  return (
    <>
      <PageHead
        title="Gestión de Notificaciones"
        sub="Vista administrador — todas las notificaciones del sistema"
        actions={
          <>
            <label className="row text-[13px] text-ink-700 cursor-pointer" style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={soloActivos}
                onChange={e => setSoloActivos(e.target.checked)}
              />
              Solo activas
            </label>
            <button onClick={onCrear} className="btn btn-primary">
              <I.Plus size={14} /> Nueva notificación
            </button>
          </>
        }
      />

      {/* KPIs */}
      <div className="kpi-grid mb-4">
        <div className="kpi">
          <div className="kpi-icon" style={{ background: 'var(--primary-100)', color: 'var(--primary-700)' }}>
            <I.Bell size={20} />
          </div>
          <div className="kpi-label">Notificaciones activas</div>
          <div className="kpi-value">{totalActivas}</div>
          <div className="kpi-trend neutral">de {items.length} totales</div>
        </div>
        <div className="kpi">
          <div className="kpi-icon" style={{ background: 'var(--accent-100)', color: 'var(--accent-700)' }}>
            <I.Eye size={20} />
          </div>
          <div className="kpi-label">Total lecturas</div>
          <div className="kpi-value">{totalLecturas.toLocaleString()}</div>
        </div>
        <div className="kpi">
          <div className="kpi-icon" style={{ background: '#dcfce7', color: '#166534' }}>
            <I.CheckCircle size={20} />
          </div>
          <div className="kpi-label">Confirmaciones</div>
          <div className="kpi-value">{totalConfirmaciones}</div>
          <div className="kpi-trend neutral">de {requierenConf} solicitadas</div>
        </div>
        <div className="kpi">
          <div className="kpi-icon" style={{ background: '#dbeafe', color: '#1e40af' }}>
            <I.Megaphone size={20} />
          </div>
          <div className="kpi-label">Comunicados</div>
          <div className="kpi-value">{items.filter(n => n.categoria === 'COMUNICADO').length}</div>
        </div>
      </div>

      {/* Filtros por categoría */}
      <div className="flex gap-1.5 mb-4 flex-wrap">
        {(['TODOS', 'COMUNICADO', 'ALERTA', 'RECORDATORIO'] as const).map(c => (
          <button
            key={c}
            className={`pill ${filtroCat === c ? 'active' : ''}`}
            onClick={() => setFiltroCat(c)}
          >
            {c === 'TODOS' ? 'Todas' : CATEGORIA_BADGE[c]?.label ?? c}
          </button>
        ))}
      </div>

      {/* Tabla */}
      {loading ? (
        <Card><div className="flex justify-center py-16"><I.Activity size={32} className="animate-pulse text-ink-300" /></div></Card>
      ) : filtrados.length === 0 ? (
        <Card><EmptyState icon="Bell" title="Sin notificaciones" sub="No hay registros que coincidan con los filtros" /></Card>
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Título</th>
                <th>Categoría</th>
                <th>Audiencia</th>
                <th className="hide-md">Creado por</th>
                <th className="hide-md">Fecha</th>
                <th>Lecturas</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtrados.map(n => (
                <React.Fragment key={n.idNotificacion}>
                  <tr style={{ opacity: n.activo ? 1 : 0.5 }}>
                    <td>
                      <div className="flex flex-col gap-0.5 max-w-xs">
                        <p className="font-semibold text-ink-900 truncate m-0">{n.titulo}</p>
                        <p
                          className="text-xs text-ink-400 truncate m-0"
                          dangerouslySetInnerHTML={{ __html: n.mensaje.replace(/<[^>]*>/g, ' ').trim() }}
                        />
                      </div>
                    </td>
                    <td>
                      <Badge kind={CATEGORIA_BADGE[n.categoria]?.kind ?? 'neutral'}>
                        {CATEGORIA_BADGE[n.categoria]?.label ?? n.categoria}
                      </Badge>
                      {n.requiereConfirmacion && (
                        <p className="text-[10px] mt-1 m-0" style={{ color: 'var(--warning)' }}>
                          Req. confirmación
                        </p>
                      )}
                    </td>
                    <td>
                      <Badge kind="indigo">{AUDIENCIA_LABEL[n.tipoAudiencia]}</Badge>
                      <p className="text-xs text-ink-400 mt-1 m-0">{audienciaDetalle(n)}</p>
                    </td>
                    <td className="hide-md whitespace-nowrap">{n.creadoPorNombre}</td>
                    <td className="hide-md text-xs text-ink-500 whitespace-nowrap">
                      {fmtFecha(n.fechaCreacion)}
                      {n.fechaExpiracion && (
                        <p className="m-0" style={{ color: 'var(--warning)' }}>
                          Exp: {n.fechaExpiracion}
                        </p>
                      )}
                    </td>
                    <td>
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="row text-ink-700 font-semibold tabular" style={{ gap: 4 }}>
                          <I.Eye size={13} className="text-ink-400" />
                          {n.totalLecturas ?? 0}
                        </span>
                        {n.requiereConfirmacion && (
                          <span className="row text-xs font-semibold tabular" style={{ gap: 4, color: '#166534' }}>
                            <I.Check size={11} />
                            {n.totalConfirmaciones ?? 0} conf.
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <Badge kind={n.activo ? 'success' : 'neutral'} dot>
                        {n.activo ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </td>
                    <td className="text-right">
                      {n.activo && confirmId !== n.idNotificacion && (
                        <button
                          onClick={() => setConfirmId(n.idNotificacion)}
                          className="sae-icon-btn"
                          style={{ width: 32, height: 32 }}
                          aria-label="Desactivar"
                        >
                          <I.Trash size={14} />
                        </button>
                      )}
                    </td>
                  </tr>

                  {confirmId === n.idNotificacion && (
                    <tr>
                      <td colSpan={8} style={{ background: 'var(--danger-bg)' }}>
                        <div className="row" style={{ gap: 12 }}>
                          <p className="text-sm flex-1 m-0" style={{ color: '#991b1b' }}>
                            ¿Desactivar «{n.titulo}»? Dejará de ser visible para los usuarios.
                          </p>
                          <button onClick={() => setConfirmId(null)} className="btn btn-ghost btn-sm">
                            Cancelar
                          </button>
                          <button
                            onClick={() => handleDesactivar(n.idNotificacion)}
                            disabled={desactivando}
                            className="btn btn-danger btn-sm"
                          >
                            {desactivando && <I.Activity size={11} className="animate-pulse" />}
                            Sí, desactivar
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────

const NotificacionesPage: React.FC = () => {
  const { user } = useAuth();
  const [vista, setVista] = useState<'lista' | 'crear'>('lista');

  const isAdmin    = user?.rol === 'ADMIN';
  const puedeCrear = ROLES_PUEDEN_CREAR.includes(user?.rol ?? '');

  if (vista === 'crear') {
    return (
      <CrearNotificacionPage
        onCancel={() => setVista('lista')}
        onSuccess={() => setVista('lista')}
      />
    );
  }

  if (isAdmin) return <VistaAdmin onCrear={() => setVista('crear')} />;
  return <VistaUsuario puedeCrear={puedeCrear} onCrear={() => setVista('crear')} />;
};

export default NotificacionesPage;
